/**
 * BlueNote crash-recovery store — every recorded chunk is streamed into
 * IndexedDB as it arrives, so a Chrome crash (which wipes the in-memory
 * blobs) costs at most ~a second of footage instead of the whole session
 * (field incident: a crash mid-recording lost everything).
 *
 * Layout:
 *  - 'sessions': { id, startedAt, segMeta: { [segNo]: { caption, durationSeconds } },
 *                  fullDurationSeconds } — updated at every cut.
 *  - 'chunks':   auto-key rows { sessionId, kind: 'seg'|'full'|'audio', segNo, blob }.
 *
 * Clean flows (publish/discard/reset) delete their session's rows; anything
 * left at provider mount is a crashed (or abandoned-draft) session offered
 * for recovery. Writes are fire-and-forget — persistence failures must never
 * affect recording.
 */

export type ChunkKind = 'seg' | 'full' | 'audio';

export interface SessionMeta {
  id: string;
  startedAt: number;
  segMeta: Record<number, { caption: string; durationSeconds: number }>;
  fullDurationSeconds: number | null;
}

export interface RecoveredSegment {
  segNo: number;
  videoBlob: Blob;
  audioBlob: Blob | null;
  caption: string;
  durationSeconds: number;
}

export interface RecoveredSession {
  meta: SessionMeta;
  segments: RecoveredSegment[];
  fullRun: { blob: Blob; durationSeconds: number } | null;
}

const DB_NAME = 'bluenote-recorder';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('sessions')) {
        db.createObjectStore('sessions', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('chunks')) {
        const chunks = db.createObjectStore('chunks', { autoIncrement: true });
        chunks.createIndex('bySession', 'sessionId');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

function tx(db: IDBDatabase, stores: string[], mode: IDBTransactionMode): IDBTransaction {
  return db.transaction(stores, mode);
}

function done(t: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error ?? new Error('idb tx failed'));
    t.onabort = () => reject(t.error ?? new Error('idb tx aborted'));
  });
}

export async function startSession(id: string): Promise<void> {
  const db = await openDb();
  const t = tx(db, ['sessions'], 'readwrite');
  t.objectStore('sessions').put({
    id,
    startedAt: Date.now(),
    segMeta: {},
    fullDurationSeconds: null,
  } satisfies SessionMeta);
  await done(t);
  db.close();
}

export async function appendChunk(sessionId: string, kind: ChunkKind, segNo: number, blob: Blob): Promise<void> {
  const db = await openDb();
  const t = tx(db, ['chunks'], 'readwrite');
  t.objectStore('chunks').add({ sessionId, kind, segNo, blob });
  await done(t);
  db.close();
}

export async function saveSegmentMeta(
  sessionId: string,
  segNo: number,
  meta: { caption: string; durationSeconds: number },
): Promise<void> {
  const db = await openDb();
  const t = tx(db, ['sessions'], 'readwrite');
  const store = t.objectStore('sessions');
  const req = store.get(sessionId);
  req.onsuccess = () => {
    const session = req.result as SessionMeta | undefined;
    if (session) {
      session.segMeta[segNo] = meta;
      store.put(session);
    }
  };
  await done(t);
  db.close();
}

export async function saveFullDuration(sessionId: string, durationSeconds: number): Promise<void> {
  const db = await openDb();
  const t = tx(db, ['sessions'], 'readwrite');
  const store = t.objectStore('sessions');
  const req = store.get(sessionId);
  req.onsuccess = () => {
    const session = req.result as SessionMeta | undefined;
    if (session) {
      session.fullDurationSeconds = durationSeconds;
      store.put(session);
    }
  };
  await done(t);
  db.close();
}

/** Session ids with persisted data, excluding the currently-active one. */
export async function listRecoverableSessions(excludeId: string | null): Promise<SessionMeta[]> {
  const db = await openDb();
  const t = tx(db, ['sessions'], 'readonly');
  const req = t.objectStore('sessions').getAll();
  await done(t);
  db.close();
  const sessions = (req.result as SessionMeta[]) ?? [];
  return sessions.filter((s) => s.id !== excludeId).sort((a, b) => b.startedAt - a.startedAt);
}

/** Rebuild a session's blobs from its persisted chunks (insertion-ordered). */
export async function loadSession(sessionId: string): Promise<RecoveredSession | null> {
  const db = await openDb();
  const t = tx(db, ['sessions', 'chunks'], 'readonly');
  const metaReq = t.objectStore('sessions').get(sessionId);
  const chunksReq = t.objectStore('chunks').index('bySession').getAll(sessionId);
  await done(t);
  db.close();
  const meta = metaReq.result as SessionMeta | undefined;
  if (!meta) return null;
  const rows = (chunksReq.result as Array<{ kind: ChunkKind; segNo: number; blob: Blob }>) ?? [];

  const bySeg = new Map<number, { seg: Blob[]; audio: Blob[] }>();
  const full: Blob[] = [];
  for (const row of rows) {
    if (row.kind === 'full') {
      full.push(row.blob);
      continue;
    }
    const entry = bySeg.get(row.segNo) ?? { seg: [], audio: [] };
    if (row.kind === 'seg') entry.seg.push(row.blob);
    else entry.audio.push(row.blob);
    bySeg.set(row.segNo, entry);
  }

  const segments: RecoveredSegment[] = [...bySeg.entries()]
    .filter(([, e]) => e.seg.length > 0)
    .sort(([a], [b]) => a - b)
    .map(([segNo, e]) => ({
      segNo,
      videoBlob: new Blob(e.seg, { type: 'video/webm' }),
      audioBlob: e.audio.length ? new Blob(e.audio, { type: 'audio/webm' }) : null,
      caption: meta.segMeta[segNo]?.caption ?? '',
      // Uncut (crashed mid-segment): estimate from the 250ms chunk cadence.
      durationSeconds: meta.segMeta[segNo]?.durationSeconds ?? Math.max(1, Math.round(e.seg.length * 0.25)),
    }));

  const fullRun =
    full.length > 0
      ? {
          blob: new Blob(full, { type: 'video/webm' }),
          // Full-run chunks arrive at 1s cadence when uncut.
          durationSeconds: meta.fullDurationSeconds ?? Math.max(1, full.length),
        }
      : null;

  return segments.length || fullRun ? { meta, segments, fullRun } : null;
}

export async function clearSession(sessionId: string): Promise<void> {
  const db = await openDb();
  const t = tx(db, ['sessions', 'chunks'], 'readwrite');
  t.objectStore('sessions').delete(sessionId);
  const index = t.objectStore('chunks').index('bySession');
  const cursorReq = index.openKeyCursor(IDBKeyRange.only(sessionId));
  const chunkStore = t.objectStore('chunks');
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (cursor) {
      chunkStore.delete(cursor.primaryKey);
      cursor.continue();
    }
  };
  await done(t);
  db.close();
}
